import { escapeHTML } from '../../utils/escape.js';
import { PERSONAS_MIN, PERSONAS_MAX } from '../../shared/constants.js';
import {
    RESERVA_SLOTS,
    getAvailableMomentos,
    getUpcomingDates,
} from '../../services/reservation.rules.js';

/**
 * Formulario de alta manual de reservas para el panel de staff ("Nueva
 * reserva"). Función de render pura: devuelve un string HTML, sin estado ni
 * efectos. El wiring (`js/admin.js`) cablea el submit del botón
 * `data-action="reserva-crear"` tras cada render del mount de reservas.
 *
 * Criterio "libre en el timing, pero respeta el día": staff elige personas/área
 * libremente y NO aplica la anticipación de 2h (puede reservar hoy / con poca
 * antelación), pero SÍ respeta la disponibilidad por día — igual que el wizard
 * público. La cascada es fecha → momento → hora:
 *  - `fecha`: un `<select>` de los próximos días (`getUpcomingDates`), no un
 *    input de fecha libre.
 *  - `momento`: sus opciones dependen de la fecha (`getAvailableMomentos` sobre
 *    esa fecha) — p. ej. un lunes ofrece sólo Merienda/Cena (abre 17hs), sin
 *    Desayuno/Almuerzo.
 *  - `hora`: los slots crudos de `RESERVA_SLOTS[momento]` (sin filtro de 2h — se
 *    evita `getValidTimeSlots`, que es la regla de clientes).
 * El cupo lo garantiza server-side la RPC vía `POST /api/reservas`. El wiring
 * (`js/admin.js`) repuebla `momento`/`hora` en cascada tras cada cambio.
 *
 * Estilos alineados a `BlocksSection` (tokens theme-aware: bg-base / bg-surface /
 * border-border-subtle / text-*; radios rounded-2xl; sin hex hardcodeado).
 *
 * Canonical momentos: Desayuno, Almuerzo, Merienda, Cena, Trasnoche.
 * Canonical areas: Vereda, Salón.
 */

const AREAS = ['Vereda', 'Salón'];

/** Clases compartidas por los inputs/selects del formulario (tokens theme-aware). */
const FIELD =
    'w-full bg-base border border-border-subtle rounded-2xl px-4 py-3 text-sm text-text-primary focus:outline-none focus:border-brand';
const FIELD_LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2';

/**
 * Opciones del select `hora` para un `momento` dado: los slots crudos de
 * `RESERVA_SLOTS` (staff "libre", sin regla de 2h ni filtro por día). Se usa en
 * el render inicial y cuando el wiring repuebla `hora` al cambiar `momento`.
 * @param {string} momento
 * @returns {string} HTML de `<option>`s (placeholder + slots).
 */
export function horaOptions(momento) {
    const slots = RESERVA_SLOTS[momento] ?? [];
    const placeholder = slots.length ? 'Elegí una hora' : 'Elegí un momento primero';
    const options = slots
        .map((h) => `<option value="${escapeHTML(h)}">${escapeHTML(h)}</option>`)
        .join('');
    return `<option value="">${placeholder}</option>${options}`;
}

/**
 * Opciones del select `momento` para una `fecha` (ISO) dada: los momentos
 * disponibles ese día según `getAvailableMomentos` (respeta el horario por día,
 * p. ej. lunes/martes sólo Merienda/Cena). Deriva el weekday desde un Date a
 * mediodía local para evitar el off-by-one por zona horaria. Se usa en el render
 * inicial y cuando el wiring repuebla `momento` al cambiar `fecha`. Sin
 * placeholder cuando hay momentos: el primero queda seleccionado (la cascada
 * puebla `hora` a partir de él).
 * @param {string} iso — fecha ISO YYYY-MM-DD
 * @returns {string} HTML de `<option>`s.
 */
export function momentoOptions(iso) {
    const momentos = iso ? getAvailableMomentos(new Date(`${iso}T12:00:00`)) : [];
    if (!momentos.length) {
        return '<option value="">Elegí una fecha primero</option>';
    }
    return momentos
        .map((m) => `<option value="${escapeHTML(m)}">${escapeHTML(m)}</option>`)
        .join('');
}

/**
 * Formulario de reserva, compartido por el alta y la edición.
 *
 * Los valores NO se interpolan acá: el wiring los setea por DOM después del
 * render (`prefillReservaForm` en admin.js). Los tres selects encadenados
 * (fecha → momento → hora) ya se repueblan por DOM cuando cambia el de arriba;
 * duplicar esa lógica en el render sería una segunda fuente de verdad para la
 * misma cascada.
 *
 * `mesasHtml` es un slot: el plano de mesas se inyecta desde afuera para que
 * este componente siga siendo un render puro sin conocer el estado del plano.
 * Elegir mesas es OPCIONAL — una reserva sin mesas asignadas es válida.
 *
 * @param {{ mode?: 'crear' | 'editar', mesasHtml?: string }} [options]
 * @returns {string} HTML de la sección (encabezado + formulario).
 */
export function NuevaReservaForm({ mode = 'crear', mesasHtml = '' } = {}) {
    const editando = mode === 'editar';
    const eyebrow = editando ? 'Edición' : 'Alta manual';
    const titulo = editando ? 'Editar reserva' : 'Nueva reserva';
    const submitAction = editando ? 'reserva-guardar' : 'reserva-crear';
    const submitLabel = editando ? 'Guardar cambios' : 'Crear reserva';
    // Cascada inicial: la primera fecha disponible define los momentos, y el
    // primer momento define las horas — todo pre-poblado en el render.
    const dates = getUpcomingDates();
    const defaultIso = dates[0]?.iso ?? '';
    const defaultMomentos = defaultIso ? getAvailableMomentos(new Date(`${defaultIso}T12:00:00`)) : [];
    const defaultMomento = defaultMomentos[0] ?? '';

    const fechaOptions = dates
        .map((d) => `<option value="${escapeHTML(d.iso)}">${escapeHTML(d.label)}</option>`)
        .join('');
    const areaOptions = AREAS.map(
        (a) => `<option value="${escapeHTML(a)}">${escapeHTML(a)}</option>`,
    ).join('');

    return `
        <section aria-label="${escapeHTML(titulo)}" class="mb-8">
            <span class="block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2">${escapeHTML(eyebrow)}</span>
            <h3 class="text-lg font-black uppercase tracking-tight text-text-primary mb-5">${escapeHTML(titulo)}</h3>

            <form class="bg-surface border border-border-subtle rounded-2xl p-6">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                    <div>
                        <label class="${FIELD_LABEL}">Nombre</label>
                        <input type="text" name="nombre" required placeholder="Ej. Ana" class="${FIELD}" />
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Apellido</label>
                        <input type="text" name="apellido" required placeholder="Ej. Gómez" class="${FIELD}" />
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Teléfono</label>
                        <input type="tel" name="telefono" required placeholder="Ej. 11 5555 5555" class="${FIELD}" />
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Fecha</label>
                        <select name="fecha" required class="${FIELD}">
                            ${fechaOptions}
                        </select>
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Momento</label>
                        <select name="momento" required class="${FIELD}">
                            ${momentoOptions(defaultIso)}
                        </select>
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Hora</label>
                        <select name="hora" required class="${FIELD}">
                            ${horaOptions(defaultMomento)}
                        </select>
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Personas</label>
                        <input type="number" name="personas" required min="${PERSONAS_MIN}" max="${PERSONAS_MAX}" placeholder="Ej. 4" class="${FIELD}" />
                    </div>
                    <div>
                        <label class="${FIELD_LABEL}">Área</label>
                        <select name="area" required class="${FIELD}">
                            <option value="">Elegí un área</option>
                            ${areaOptions}
                        </select>
                    </div>
                </div>

                <div class="mb-5">
                    <label class="${FIELD_LABEL}">
                        Comentarios <span class="font-normal tracking-normal normal-case">(opcional)</span>
                    </label>
                    <textarea name="comentarios" rows="2" maxlength="300"
                        placeholder="Ej. Cumpleaños, sin TACC, silla de bebé"
                        class="${FIELD} resize-y"></textarea>
                </div>

                ${mesasHtml
                    ? `<div class="border-t border-border-subtle pt-5 mb-5">
                        <span class="block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-3">
                            Mesas <span class="font-normal tracking-normal normal-case">(opcional)</span>
                        </span>
                        ${mesasHtml}
                    </div>`
                    : ''}

                <button type="submit" data-action="${submitAction}"
                    class="w-full sm:w-auto px-8 py-3 bg-brand text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg active:scale-95 transition-transform disabled:opacity-60 disabled:pointer-events-none">
                    ${escapeHTML(submitLabel)}
                </button>
            </form>
        </section>
    `;
}
